import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { VStack } from "../../../../styled-system/jsx";
import { useBulkAdScheduledActionsUpdate } from "../../../modules/advertising/api/useBulkAdScheduledActionsUpdate";
import { AdToolPanel } from "../AdToolPanel";
import { Button } from "../Chat";

const formatDate = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return isoString;
  return date.toLocaleString();
};

const buildDescribeFieldChange = (t) => (field) => {
  const name = t(($) => $.chat.scheduleBudget["Budget"]);
  if (!field || typeof field !== "object") {
    return { name, description: t(($) => $.chat.scheduleBudget["Update"]) };
  }

  const changeTypeLabel =
    field.change_type === "percentage_change" ? t(($) => $.chat.scheduleBudget["Increase by"])
    : field.change_type === "flat_change" ? t(($) => $.chat.scheduleBudget["Change by"])
    : t(($) => $.chat.scheduleBudget["Change by"]);
  let changeValue = field.change_value;
  if (typeof changeValue === "number") {
    const percent = (changeValue - 1) * 100;
    changeValue = `${percent.toFixed(2).replace(/\.00$/, "")}%`;
  } else {
    changeValue = String(changeValue ?? "");
  }
  const rollback =
    field.rollback ?
      t(($) => $.chat.scheduleBudget["Rollback enabled"])
    : t(($) => $.chat.scheduleBudget["No rollback"]);

  return { name, description: `${changeTypeLabel} ${changeValue}${rollback ? ` • ${rollback}` : ""}` };
};

const AdScheduleBudgetToolMessage = ({ adIds, payload }) => {
  const { t } = useTranslation(["chat"]);
  const mutation = useBulkAdScheduledActionsUpdate();

  const normalizedPayload = useMemo(() => {
    if (!payload || typeof payload !== "object") return null;

    const { channel, start_date, end_date, fields, gvadvertisements_set, status } = payload;
    if (!channel || !start_date || !end_date || !fields || !Array.isArray(fields) || !gvadvertisements_set) {
      return null;
    }

    return {
      channel,
      start_date,
      end_date,
      status: typeof status === "string" && status.length ? status : "scheduled",
      fields: fields.map((field) => ({
        field_name: field.field_name,
        change_type: field.change_type,
        change_value: typeof field.change_value === "number" ? field.change_value : Number(field.change_value),
        rollback: Boolean(field.rollback),
      })),
      gvadvertisements_set: gvadvertisements_set.map((id) => Number(id)),
    };
  }, [payload]);

  const scheduleSummary = useMemo(() => {
    if (!normalizedPayload) return [];
    const describeFieldChange = buildDescribeFieldChange(t);
    const descriptions = [];
    let currentGroup = null;

    normalizedPayload.fields.forEach((field) => {
      const { name, description } = describeFieldChange(field);
      if (!currentGroup || currentGroup.name !== name) {
        if (currentGroup) descriptions.push(currentGroup);
        currentGroup = { name, details: [description] };
      } else if (!currentGroup.details.includes(description)) {
        currentGroup.details.push(description);
      }
    });

    if (currentGroup) descriptions.push(currentGroup);

    return descriptions;
  }, [normalizedPayload, t]);

  const startText = normalizedPayload ? formatDate(normalizedPayload.start_date) : "";
  const endText = normalizedPayload ? formatDate(normalizedPayload.end_date) : "";
  const panelAdIds = useMemo(() => {
    if (adIds && adIds.length) return adIds;
    if (normalizedPayload?.gvadvertisements_set?.length) return normalizedPayload.gvadvertisements_set;
    return [];
  }, [adIds, normalizedPayload?.gvadvertisements_set]);

  return (
    <AdToolPanel
      adIds={panelAdIds}
      renderAfterList={() => (
        <div
          className={css({
            width: "full",
            backgroundColor: "#f7f7fa",
            borderRadius: "8px",
            padding: "12px",
            border: "1px solid #e2e2e5",
            fontSize: "12px",
            color: "#333",
            whiteSpace: "normal",
          })}
        >
          <div className={css({ fontSize: "13px", fontWeight: 600, marginBottom: "6px" })}>
            {t(($) => $.chat.scheduleBudget["Scheduled budget update"])}
          </div>
          {normalizedPayload ?
            <VStack alignItems="flex-start" gap={2}>
              <div>{t(($) => $.chat.scheduleBudget["Channel: {channel}"], { channel: normalizedPayload.channel })}</div>
              <div>{t(($) => $.chat.scheduleBudget["Starts: {date}"], { date: startText })}</div>
              <div>{t(($) => $.chat.scheduleBudget["Ends: {date}"], { date: endText })}</div>
              <div className={css({ fontWeight: 600, marginTop: "6px" })}>
                {t(($) => $.chat.scheduleBudget["Changes"])}
              </div>
              {scheduleSummary.length ?
                scheduleSummary.map(({ name, details }) => (
                  <div key={name}>
                    {name}: {details.join("; ")}
                  </div>
                ))
              : <div>{t(($) => $.chat.scheduleBudget["No field changes provided."])}</div>}
            </VStack>
          : <div>{t(($) => $.chat.scheduleBudget["Schedule details unavailable."])}</div>}
        </div>
      )}
      renderActions={({ isLoading }) => {
        const targetAdIds = normalizedPayload?.gvadvertisements_set ?? [];

        const handleSchedule = () => {
          if (!normalizedPayload || !targetAdIds.length || mutation.isPending) return;
          targetAdIds.forEach((adId, index) => {
            mutation.mutate({
              data: {
                channel: normalizedPayload.channel,
                start_date: normalizedPayload.start_date,
                end_date: normalizedPayload.end_date,
                status: normalizedPayload.status,
                gvadvertisements_set: [adId],
                fields: normalizedPayload.fields.map(({ field_name, change_type, change_value }) => ({
                  field_name,
                  change_type,
                  change_value,
                })),
              },
              isLast: index === targetAdIds.length - 1,
            });
          });
        };

        const isButtonDisabled = !normalizedPayload || !targetAdIds.length || mutation.isPending || isLoading;

        const buttonLabel =
          mutation.isPending ?
            t(($) => $.chat.scheduleBudget["Scheduling..."])
          : t(($) => $.chat.scheduleBudget["Schedule Budget Update"]);

        return (
          <Button onClick={handleSchedule} disabled={isButtonDisabled}>
            {buttonLabel}
          </Button>
        );
      }}
    />
  );
};

export { AdScheduleBudgetToolMessage };
